import { useState, ChangeEvent, useMemo, useEffect } from 'react';
import { Row, Col } from 'react-bootstrap';
import {
  faFilter,
  faPlus,
  faClipboardList
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useSelector, useDispatch } from 'react-redux';
import Button from 'components/base/Button';
import SearchBox from 'components/common/SearchBox';
import CompanyFilter from 'components/common/CompanyFilter';
import CreateRequestModal from 'components/modals/CreateRequestModal';
import RequestTable from 'components/devices/RequestTable';
import { RootState, AppDispatch } from 'store';
import { fetchCompanies } from 'store/companySlice';

const RequestPage = () => {
  const dispatch = useDispatch<AppDispatch>();

  // State
  const [openCreateModal, setOpenCreateModal] = useState(false);
  const [openFilterModal, setOpenFilterModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCompanyId, setSelectedCompanyId] = useState<number | null>(
    null
  );
  const [showFilterPanel, setShowFilterPanel] = useState(false);

  // Get auth state and companies from Redux
  const auth = useSelector((state: RootState) => state.auth);
  const companies = useSelector((state: RootState) => state.companies.data);

  // Check if user is super admin
  const isSuperAdmin = useMemo(() => auth.role === 'super_user', [auth.role]);

  // Fetch companies if user is super admin
  useEffect(() => {
    if (isSuperAdmin) {
      dispatch(fetchCompanies());
    }
  }, [isSuperAdmin, dispatch]);

  const handleSearchInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  const handleCompanySelect = (companyId: number | null) => {
    setSelectedCompanyId(companyId);
  };

  return (
    <div>
      <div className="mb-6">
        <h2
          className="mb-0"
          style={{
            fontSize: '1.5rem',
            fontWeight: '600',
            color: '#374151',
            letterSpacing: '-0.025em'
          }}
        >
          <FontAwesomeIcon
            icon={faClipboardList}
            className="me-2 text-primary"
          />
          Requests
        </h2>
        <Row className="g-3 justify-content-end mb-0">
          <Col xs="auto">
            <div className="d-flex gap-2">
              <button
                className="btn d-flex align-items-center gap-2"
                style={{
                  backgroundColor: 'white',
                  border: '1px solid #e5e7eb',
                  borderRadius: '0.375rem',
                  padding: '0.375rem 0.75rem',
                  fontSize: '0.875rem',
                  color: '#374151',
                  fontWeight: '400'
                }}
                onClick={() => setShowFilterPanel(!showFilterPanel)}
              >
                <FontAwesomeIcon
                  icon={faFilter}
                  style={{ fontSize: '0.875rem' }}
                />
                <span>Filter</span>
              </button>
              <SearchBox
                placeholder="Search by request title or requester"
                value={searchQuery}
                onChange={handleSearchInputChange}
              />
              <Button
                variant="primary"
                startIcon={<FontAwesomeIcon icon={faPlus} />}
                onClick={() => setOpenCreateModal(true)}
                style={{
                  fontSize: '0.875rem',
                  padding: '0.375rem 0.75rem',
                  fontWeight: '500'
                }}
              >
                Add Request
              </Button>
            </div>
          </Col>
        </Row>

        {/* Filter Panel */}
        {showFilterPanel && isSuperAdmin && companies.length > 0 && (
          <div className="mb-3 mt-3">
            <div className="d-flex gap-2">
              <CompanyFilter
                companies={companies}
                selectedCompanyId={selectedCompanyId}
                onCompanySelect={handleCompanySelect}
              />
            </div>
          </div>
        )}

        {/* Request Table */}
        <div className="mt-3">
          <RequestTable
            searchQuery={searchQuery}
            selectedCompanyId={selectedCompanyId}
          />
        </div>

        {/* Modal: Create Request */}
        <CreateRequestModal
          show={openCreateModal}
          handleClose={() => setOpenCreateModal(false)}
        />
      </div>
    </div>
  );
};

export default RequestPage;
